import { useState, useRef } from "react";
import { useHomeownerAuth } from "./useHomeownerAuth";
import HomeownerGate from "./HomeownerGate";
import { supabaseAnon as supabase } from "../../lib/supabase";
import { heicToJpeg } from "../../lib/heicToJpeg";
import { authTriple, hoPortalNotify, portalNotifyMessage } from "../../lib/homeownerRpc";

interface UploadedFile {
  id: string;
  name: string;
  status: "uploading" | "done" | "error";
}

export default function UploadPortal() {
  const auth = useHomeownerAuth();
  const { deal } = auth;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // Files go to the homeowner-uploads drop folder; one call to
  // ho-portal-notify then files them on the deal's Documents tab and alerts
  // the team. (This page used to text our own number, which Twilio refused.)
  const handleFiles = async (fileList: FileList | null) => {
    if (!deal || !fileList || fileList.length === 0) return;
    const picked = Array.from(fileList);
    const stamp = Date.now();
    const entries: UploadedFile[] = picked.map((f, i) => ({ id: `${stamp}-${i}`, name: f.name, status: "uploading" }));
    setFiles((prev) => [...prev, ...entries]);
    setUploadError(null);
    const setStatus = (id: string, status: UploadedFile["status"]) =>
      setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, status } : f)));

    const stored: Array<{ id: string; path: string }> = [];
    await Promise.all(picked.map(async (p, i) => {
      const id = entries[i].id;
      try {
        // Convert iPhone HEIC/HEIF to JPEG so homeowner uploads render in every
        // browser the office team uses. No-op for non-HEIC files.
        const file = await heicToJpeg(p);
        const path = `${deal.id}/${stamp + i}-${file.name}`;
        const { error } = await supabase.storage
          .from("homeowner-uploads")
          .upload(path, file, { upsert: false });
        if (error) setStatus(id, "error");
        else stored.push({ id, path });
      } catch {
        setStatus(id, "error");
      }
    }));
    if (stored.length === 0) {
      setUploadError(portalNotifyMessage("failed"));
      return;
    }

    const res = await hoPortalNotify(authTriple(deal), "upload", {
      source: "upload_page",
      files: stored.map((s) => ({ path: s.path })),
    });
    const results = (res.data?.files ?? []) as Array<{ path: string; ok: boolean }>;
    for (const s of stored) {
      const r = results.find((x) => x.path === s.path);
      setStatus(s.id, r?.ok ? "done" : "error");
    }
    if (!res.ok) {
      setUploadError(portalNotifyMessage(res.code));
    } else if (stored.length < picked.length || results.some((r) => !r.ok)) {
      setUploadError("Some files didn't come through (marked ✗). Please try those again, or text them to (470) 823-8816.");
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <HomeownerGate {...auth} title="Upload Documents">
      {deal && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h1 className="text-lg font-bold text-bardin-ink mb-1">Upload Documents</h1>
            <p className="text-sm text-gray-500 mb-4">
              Send us photos or documents related to your project at{" "}
              {deal.property_street}.
            </p>

            {/* Drop zone */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition
                ${dragActive ? "border-bardin-orange bg-bardin-orange/5" : "border-gray-300 hover:border-bardin-teal"}`}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="text-3xl mb-2">📄</div>
              <p className="text-sm font-medium text-bardin-ink">
                Tap to select files or drag & drop
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Photos, PDFs, insurance documents, check images
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
            </div>

            {/* Common upload types */}
            <div className="mt-4 grid grid-cols-2 gap-2">
              {[
                { icon: "📋", label: "Insurance Scope" },
                { icon: "💳", label: "Check Photo" },
                { icon: "📸", label: "Damage Photo" },
                { icon: "📝", label: "Signed Document" },
              ].map((t) => (
                <button
                  key={t.label}
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm text-bardin-ink hover:bg-bardin-sky/50 transition"
                >
                  <span>{t.icon}</span>
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Upload progress */}
          {files.length > 0 && (
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h2 className="font-semibold text-bardin-ink mb-3">Uploads</h2>
              <div className="space-y-2">
                {files.map((f) => (
                  <div key={f.id} className="flex items-center gap-3 text-sm">
                    <span>
                      {f.status === "uploading" ? "⏳" : f.status === "done" ? "✓" : "✗"}
                    </span>
                    <span className="flex-1 truncate">{f.name}</span>
                    <span
                      className={
                        f.status === "done"
                          ? "text-green-600"
                          : f.status === "error"
                          ? "text-red-500"
                          : "text-gray-400"
                      }
                    >
                      {f.status === "uploading"
                        ? "Uploading..."
                        : f.status === "done"
                        ? "Done"
                        : "Failed"}
                    </span>
                  </div>
                ))}
              </div>
              {uploadError && (
                <p className="text-xs text-red-600 mt-3">{uploadError}</p>
              )}
            </div>
          )}

          <div className="text-center text-sm text-gray-400 pb-6">
            You can also text photos directly to{" "}
            <a href="sms:+14708238816" className="text-bardin-teal font-medium">
              (470) 823-8816
            </a>
          </div>
        </div>
      )}
    </HomeownerGate>
  );
}
